import { useQuery } from '@tanstack/react-query';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';

/**
 * QA-464 등급 할인 제외(user-service discountExclusions) — 고도몰 회원등급 [추가 할인] 예외 조건과 같다. 걸린 상품은 등급 할인을
 * 받지 않는다. 카테고리는 하위 카테고리 상품도 제외.
 */
export interface GradeDiscountExclusions {
  vendorIds: string[];
  categoryIds: string[];
  brandIds: string[];
  productIds: string[];
}

export interface GradeItem {
  gradeId: string;
  gradeCode: string;
  gradeName: string;
  priority: number;
  active: boolean;
  memberCount: number;
  /**
   * 등급 일괄 할인율(%) 0~100. R6 O — 기업회원 등급가 모델(상품별 B2B 가격 부재).
   * BE V56 member_grades.discount_rate(DEFAULT 0.00). generated 타입 codegen-lag 동안엔
   * 응답 payload 가 키를 누락할 수 있어 구조적 안전 읽기(`?? 0`)로 폴백한다.
   */
  discountRate: number;
  /**
   * 등급 추가 적립률(%) 0~100. B2B(2026-06-20) member_grades.mileage_rate(DEFAULT 0.00).
   * 기업 등급에만 의미가 있으며, codegen-lag 동안 키 누락 시 0 폴백.
   */
  mileageRate: number;
  /**
   * 무통장입금 허용 여부. member_grades.allow_bank_transfer(DEFAULT TRUE).
   * 개인/BASIC 은 항상 true(현행 회귀 0), 기업 등급만 false 로 차단 가능.
   * 키 누락 시 안전 폴백 = true(현행 동작 유지).
   */
  allowBankTransfer: boolean;
  /** 할인 제외. 응답에 키가 없으면(BE 반영 전) null — 화면은 설정 버튼을 막는다. */
  discountExclusions: GradeDiscountExclusions | null;
}

const ids = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const exclusionsOf = (v: unknown): GradeDiscountExclusions | null => {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  return { vendorIds: ids(o.vendorIds), categoryIds: ids(o.categoryIds), brandIds: ids(o.brandIds), productIds: ids(o.productIds) };
};

export interface GradesQueryParams {
  page?: number;
  size?: number;
}

export interface GradesPage {
  content: ReadonlyArray<GradeItem>;
  meta: ApiMeta;
  totalPages: number;
  totalElements: number;
}

export const gradesQueryKey = (params: GradesQueryParams) =>
  ['admin', 'user-grades', params] as const;

export const useUserGradesQuery = (params: GradesQueryParams) =>
  useQuery<GradesPage>({
    queryKey: gradesQueryKey(params),
    queryFn: async () => {
      const query: Record<string, string | number> = {};
      if (params.page != null) query.page = params.page - 1;
      if (params.size != null) query.size = params.size;
      const call = apiClient.request as unknown as (...a: unknown[]) => Promise<[unknown, ApiMeta]>;
      const [data, meta] = await call('get', '/api/admin/v1/user-grades', { params: { query } });
      // 정본 와이어: data.items + meta.pagination (라이브 프로브 2026-05-31).
      // content/totalElements 는 Spring Page 직렬화 폴백.
      const payload = data as {
        items?: Array<
          Partial<GradeItem> & {
            discountRate?: number | null;
            mileageRate?: number | null;
            allowBankTransfer?: boolean | null;
          }
        >;
        content?: Array<
          Partial<GradeItem> & {
            discountRate?: number | null;
            mileageRate?: number | null;
            allowBankTransfer?: boolean | null;
          }
        >;
        totalPages?: number;
        totalElements?: number;
      };
      // discountRate/mileageRate 가 응답에 없을 수 있어(codegen-lag/구버전 BE) 0 폴백으로 정규화한다.
      // allowBankTransfer 누락 시엔 현행 동작(허용)을 보존하기 위해 true 로 폴백한다.
      const raw = payload.items ?? payload.content ?? [];
      const content = raw.map(
        (g): GradeItem => ({
          gradeId: g.gradeId ?? '',
          gradeCode: g.gradeCode ?? '',
          gradeName: g.gradeName ?? '',
          priority: g.priority ?? 0,
          active: g.active ?? false,
          memberCount: g.memberCount ?? 0,
          discountRate: typeof g.discountRate === 'number' ? g.discountRate : 0,
          mileageRate: typeof g.mileageRate === 'number' ? g.mileageRate : 0,
          allowBankTransfer: typeof g.allowBankTransfer === 'boolean' ? g.allowBankTransfer : true,
          discountExclusions: exclusionsOf((g as { discountExclusions?: unknown }).discountExclusions),
        }),
      );
      const pagination = meta?.pagination;
      return {
        content,
        meta,
        totalPages: pagination?.totalPages ?? payload.totalPages ?? 1,
        totalElements: pagination?.totalItems ?? payload.totalElements ?? content.length,
      };
    },
  });
